/** @jsxImportSource @emotion/react */
import { useState } from 'react';
import { Stack } from '../../../design-system/layout/Stack';
import { PipelineKpiRow } from '../../organisms/PipelineKpiRow/PipelineKpiRow';
import type { PipelineKpi } from '../../organisms/PipelineKpiRow/PipelineKpiRow';
import { DailyScheduleBoard } from '../../organisms/DailyScheduleBoard/DailyScheduleBoard';
import { RunHeatmap } from '../../organisms/RunHeatmap/RunHeatmap';
import type { RunHeatmapRow } from '../../organisms/RunHeatmap/RunHeatmap';
import { ModuleHealthTable } from '../../organisms/ModuleHealthTable/ModuleHealthTable';
import type { ModuleHealth, ScheduleRow } from '../../types';

/** 이 화면이 필요로 하는 데이터. 공급 방식은 미정 (현재 데모 데이터). */
export interface DashboardData {
  kpis: PipelineKpi[];
  scheduleRows: ScheduleRow[];
  nowHour: number;
  scheduleDateLabel: string;
  /** 편성표 선택 날짜가 오늘인지 (now-line·다음 날 버튼 판단). */
  scheduleIsToday: boolean;
  heatmapRows: RunHeatmapRow[];
  healthRows: ModuleHealth[];
}

export interface DashboardScreenProps {
  data: DashboardData;
  /** 히트맵 셀·편성표 막대 클릭 → 해당 Job 상세로 이동. */
  onOpenJob?: (jobId: number) => void;
  onOpenHistory?: (moduleName: string) => void;
  /** 편성표 모듈명 클릭 → 모듈 관리 화면으로 이동. */
  onOpenModule?: (moduleName: string) => void;
  /** 편성표 날짜 이동. */
  onSchedulePrevDay?: () => void;
  onScheduleNextDay?: () => void;
  onScheduleToday?: () => void;
}

/** 대시보드 = 파이프라인 운영 관제. 수집량이 아니라 운영 상태가 핵심. */
export function DashboardScreen({
  data,
  onOpenJob,
  onOpenHistory,
  onOpenModule,
  onSchedulePrevDay,
  onScheduleNextDay,
  onScheduleToday,
}: DashboardScreenProps) {
  const [problemsOnly, setProblemsOnly] = useState(false);
  const [searchText, setSearchText] = useState('');

  return (
    <Stack gap="md">
      <PipelineKpiRow items={data.kpis} />

      {/* 각 패널은 전체 폭 · 접기 가능 (제목 클릭) */}
      <DailyScheduleBoard
        rows={data.scheduleRows}
        nowHour={data.nowHour}
        dateLabel={data.scheduleDateLabel}
        isToday={data.scheduleIsToday}
        onPrevDay={onSchedulePrevDay}
        onNextDay={onScheduleNextDay}
        onToday={onScheduleToday}
        onSlotClick={onOpenJob}
        onModuleClick={onOpenModule}
      />
      <RunHeatmap
        rows={data.heatmapRows}
        subLabel="최근 20회 · 모듈별"
        onCellClick={onOpenJob}
        onModuleClick={onOpenHistory}
        hint="모듈명 클릭 → 수집 이력 · 셀 클릭 → 해당 Job 상세"
      />

      {/* 모듈 헬스 표 (검색·필터) */}
      <ModuleHealthTable
        rows={data.healthRows}
        problemsOnly={problemsOnly}
        onProblemsOnlyChange={setProblemsOnly}
        searchText={searchText}
        onSearchChange={setSearchText}
        onOpenHistory={onOpenHistory}
      />
      {/* TODO: problemsOnly·searchText를 healthRows 조회에 연결 — 현재는 전체 표시 */}
    </Stack>
  );
}
